import React, { useContext, useEffect, useState } from "react";

import SmartForm from '@component/smartForm/SmartForm';
import SmartTextInput from '@component/smartForm/SmartInputText';
import SmartImageUpload from './smartForm/SmartImageUpload';
import SmartContainer from './smartForm/SmartContainer';
import SmartSwitchInput from "./smartForm/SmartSwitchInput";
import SmartDateInput from "@component/smartForm/SmartDateInput";


import { useShopItemQuery } from "@query/shopQuery";
import { useClubEventAllQuery } from "@query/clubQuery"
import { AccountContext } from "@context/AccountProvider";
import { useShopItemUpdateMutation } from "@mutation/shopMutation";
import { AlertContext } from "@context/AlertProvider";
import { useTranslation } from "react-i18next";
import { removeEmptyValues } from '@utils/generalUtils';
import { createLengthInRangeValidator, createLengthMaxValidator, createNumberInRangeValidator, notEmptyValidator } from "@component/smartForm/fieldValidators"
import { mapRewardedActivityEvent, RewardedActivityEvents } from "@service/rewardedActivityService"
import { useTheme } from '@mui/material/styles'; // Importujte useTheme hook, pokud nejste v komponentě, kde je theme implicitně dostupná

import Box from '@mui/material/Box';
import { Typography } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StarsIcon from '@mui/icons-material/Stars';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

function ShopItemForm({ uuid, initialIsEdit = false }) {

    const { club } = useContext(AccountContext);
    const shopItemQuery = useShopItemQuery(uuid);
    const clubEventAllQuery = useClubEventAllQuery(club.uuid)
    const shopItemUpdateMutation = useShopItemUpdateMutation(club?.uuid, uuid);
    const [shopItemModel, setShopItemModel] = useState();
    const [clubEvents, setClubEvents] = useState();
    const { addAlertBanner } = useContext(AlertContext);
    const { t } = useTranslation(["main"]);
    const theme = useTheme();

    const nameValidator = createLengthInRangeValidator(3, 50)
    const descValidator = createLengthMaxValidator(500)
    const priceCurrencyValidator = createNumberInRangeValidator(1, 1000000)
    const pricePointsValidator = createNumberInRangeValidator(1, 10000000)
    const quantityValidator = createNumberInRangeValidator(1, 1000000)

    useEffect(() => {
        if (shopItemQuery.data?.data) {
            const item = shopItemQuery.data.data;
            console.log("SHOP ITEM", item)
            setShopItemModel({
                ...item,
                file_image_uuid: item.file_image?.uuid,
                available_from: item.available_from,
                available_to: item.available_to,
            });
        }
        if (clubEventAllQuery.data?.data) {
            setClubEvents(clubEventAllQuery.data.data)
        }

    }, [shopItemQuery.data, clubEventAllQuery.data]);


    const handleSave = (newModel) => {
        const cleaned = removeEmptyValues({
            name: newModel.name,
            description: newModel.description,
            file_image_uuid: newModel.file_image_uuid,
            price_in_currency: Number(newModel.price_in_currency),
            price_in_points: Number(newModel.price_in_points),
            max_available_quantity: Number(newModel.max_available_quantity),
            is_global_available: newModel.is_global_available,
            available_from: newModel.available_from,
            available_to: newModel.available_to
        });

        shopItemUpdateMutation.mutate({ data: cleaned }, {
            onSuccess: () => addAlertBanner(t("main:saveSuccess"), "success"),
            onError: () => addAlertBanner(t("main:saveError"), "error")
        });
        shopItemQuery.refetch()
    };

    function getClubEventsVsProductPointPriceHelperText(clubEvents, pointValue) {
        if (!clubEvents || clubEvents.length === 0 || !pointValue) return "";

        var textRows = [];
        clubEvents.forEach(clubEvent => {
            const passedRewardedActivity = mapRewardedActivityEvent(clubEvent.event.name);
            if (!passedRewardedActivity || passedRewardedActivity.name === RewardedActivityEvents.WRITE_REVIEW.name) return;
            const eventCountForPointsEarned = Math.ceil(pointValue / clubEvent.reward);
            textRows.push(
                t(`shop:priceInPointsHelper.${passedRewardedActivity.translationKey}`, { "count": eventCountForPointsEarned })
            );
        });

        return t("shop:priceInPointsHelper.intro", { "points": pointValue }) + (textRows.length > 0 ? textRows.join(" " + t('or') + " ") : "");
    }

    function renderAvailibleBox() {
        if (shopItemModel?.is_available === undefined)
            return <></>
        if (shopItemModel.is_available) {
            return (
                <>
                    <CheckCircleIcon color="success" fontSize="large" />
                    <Typography variant="body1"  >
                        Lze zakoupit
                    </Typography>
                </>
            )
        }
        return (
            <>
                <CancelIcon color="error" fontSize="large" />
                <Typography variant="body1"  >
                    Nelze zakoupit
                </Typography>
            </>
        )
    }

    return (
        <SmartForm
            formModel={shopItemModel}
            setFormModel={setShopItemModel}
            onSave={handleSave}
            isLoading={shopItemQuery.isLoading}
            contentSx={{ gap: 2.5 }}
            isEdit={initialIsEdit}
            buttonPanelSx={{
                top: "10px",
                right: "10px",
            }}
        >
            <SmartContainer
                contentSx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 2.5 }}
                sx={{ width: "100%" }}>

                <SmartImageUpload
                    imageSx={{
                        width: "auto",
                        height: "100%",
                        objectFit: "contain",
                        backgroundColor: "#fff"
                    }}
                    field="file_image_uuid" />
                <SmartContainer
                    contentSx={{ px: 2, display: "flex", gap: 2.5, flexDirection: "column" }}
                    sx={{ width: "100%" }}>
                    <SmartTextInput field="name" valueSx={{ fontSize: theme.typography.h5 }} label={t("shop:productName")} validator={nameValidator} />
                    <Box sx={{
                        display: "flex",
                        width: "100%",
                        gap: 1,
                        flexDirection: "row",
                        px: 0
                    }}>
                        <Box sx={{
                            border: "1px solid",
                            borderColor: theme.palette.grey[200],
                            flex: 1,
                            height: "auto",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: 2,
                            gap: 1,
                            py: 2,
                            backgroundColor: theme.palette.grey[100]
                        }}>

                            <Typography variant="h3">
                                {shopItemModel?.sold_quantity}
                            </Typography>
                            <Typography variant="body1">
                                Prodaných produktů
                            </Typography>
                        </Box>

                        <Box sx={{
                            border: "1px solid",
                            borderColor: theme.palette.grey[200],
                            flex: 1,
                            height: "auto",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: 2,
                            gap: 1,
                            py: 2,
                            backgroundColor: theme.palette.grey[100]
                        }}>
                            {renderAvailibleBox()}
                        </Box>
                    </Box>

                    <SmartContainer contentSx={{
                        display: "flex",
                        flexDirection: "row",
                        // backgroundColor: "lightgray",
                        p: 0,
                        gap: 1,
                        borderRadius: 1

                    }}>
                        <SmartTextInput sx={{ flex: 1 }}
                            valueSx={{ fontSize: theme.typography.subtitle1 }}
                            field="price_in_points"
                            startIcon={<StarsIcon />}
                            label={t("shop:priceInPoints")}
                            type="number"
                            helperText={getClubEventsVsProductPointPriceHelperText(clubEvents, shopItemModel?.price_in_points)}
                            validator={pricePointsValidator} />

                        <SmartTextInput sx={{ flex: 1 }}
                            valueSx={{ fontSize: theme.typography.subtitle1 }}
                            field="price_in_currency"
                            helperText={t("shop:pointsInCurrencyHelper")}
                            startIcon={<MonetizationOnIcon />}
                            label={t("shop:price")} type="number" validator={priceCurrencyValidator} />
                    </SmartContainer>
                    <SmartTextInput field="description" label={t("shop:description")} multiline minRows={4} validator={descValidator} />
                    <SmartContainer contentSx={{
                        display: "flex",
                        flexDirection: "row",
                        // backgroundColor: "lightgray",
                        p: 0,
                        gap: 1,
                        borderRadius: 1

                    }}>
                        <SmartDateInput sx={{ flex: 1 }} startIcon={<CalendarMonthIcon />} field="available_from" label={t("shop:availableFrom")} validator={notEmptyValidator} />
                        <SmartDateInput sx={{ flex: 1 }} startIcon={<CalendarMonthIcon />} field="available_to" label={t("shop:availableTo")} validator={notEmptyValidator} />
                    </SmartContainer>
                    <SmartContainer contentSx={{
                        display: "flex",
                        flexDirection: "row",
                        p: 0,
                        gap: 1

                    }}>
                        <SmartTextInput
                            sx={{ flex: 1 }}
                            field="max_available_quantity"
                            label={t("shop:maxAvailable")}
                            type="number"
                            validator={quantityValidator}
                            helperText={t("shop:availableQuentityHelperText")} />
                        <SmartSwitchInput
                            helperText={t("shop:manualAvailibilityHelperText")}
                            sx={{ flex: 1 }}
                            field="is_global_available"
                            label={t("shop:availability")}
                            trueValueLabel={t("shop:available")}
                            falseValueLabel={t("shop:unavailable")} />
                    </SmartContainer>
                </SmartContainer>

            </SmartContainer>
        </SmartForm >
    );
}

export default ShopItemForm;
